import { SecondaryItem, ESecondaryItemType } from "@similarweb/ui-components";

import type { ISwSettings } from "app/@types/ISwSettings";
import { isHidden, isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { DigitalSuiteGenericNavBarBody } from "app/components/sidebar3/secondary-side-bar/components/DigitalSuiteGenericNavBarBody";
import { DigitalSuiteGenericBarHeader } from "app/components/sidebar3/secondary-side-bar/components/DigitalSuiteGenericNavBarHeader";
import type { ISecondaryBarContents } from "app/components/sidebar3/secondary-side-bar/types/ISecondaryBarContents";
import type { INavItem } from "app/components/sidebar3/types";
import {
    AI_CHANNELS_ROUTES,
    getAiAdsAvailability,
    SOCIAL_CHANNELS_ROUTES,
} from "app/pages/digital-marketing/ad-intelligence";
import { hasOnlySearchAvailable } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { applyNavItemPermissions } from "app/pages/workspace/common/workspacesUtils";
import { openHooksModalV5 } from "app/services/ModalService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { ROUTES as advertiserActivityRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/advertiserActivity/advertiserActivityConstants";
import { ROUTES as displayAdvertisingRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingConstants";
import { ROUTES as searchAdvertisingRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/searchAdvertising/searchAdvertisingConstants";

export type AANavBarRouteOverrides = {
    overview?: string;
    display?: Partial<Record<keyof typeof displayAdvertisingRoutes, string>>;
    search?: Partial<Record<keyof typeof searchAdvertisingRoutes, string>>;
    social?: { ads?: string };
    ai?: { ads?: string };
};

const getIsNonSearchGroupHidden = (): boolean => {
    return isHidden((swSettings as ISwSettings).components.AdIntelligenceMediaPartners) || hasOnlySearchAvailable();
};

const displayAdvertisingStateNames = Object.fromEntries(
    Object.entries(displayAdvertisingRoutes).map(([key, route]) => [key, route.stateName]),
) as Record<keyof typeof displayAdvertisingRoutes, string>;

const getDisplayGroup = (
    isGroupHidden: boolean,
    routes: Record<keyof typeof displayAdvertisingRoutes, string> = displayAdvertisingStateNames,
): INavItem => ({
    title: "ad.intelligence.display.title",
    name: "display",
    hidden: isGroupHidden,
    subItems: [
        {
            title: "ad.intelligence.overview.title",
            name: "overview",
            state: routes.overview,
            options: {
                isUSStatesSupported: true,
                isVirtualSupported: false,
            },
            overrideParams: {
                webSource: devicesTypes.TOTAL,
            },
            lockIcon: false,
            hidden: false,
        },
        {
            title: "ad.intelligence.display.advertising.campaigns.title",
            name: "campaigns",
            state: routes.campaigns,
            options: {
                isUSStatesSupported: false,
                isVirtualSupported: false,
            },
            overrideParams: {
                webSource: devicesTypes.TOTAL,
            },
            lockIcon: false,
            hidden: false,
        },
        {
            title: "ad.intelligence.display.advertising.ads.title",
            name: "ads",
            state: routes.ads,
            options: {
                isUSStatesSupported: false,
                isVirtualSupported: false,
            },
            overrideParams: {
                webSource: devicesTypes.TOTAL,
            },
            lockIcon: false,
            hidden: false,
        },
        {
            title: "ad.intelligence.display.advertising.publishers.title",
            name: "publishers",
            state: routes.publishers,
            options: {
                isUSStatesSupported: false,
                isVirtualSupported: false,
            },
            overrideParams: {
                webSource: devicesTypes.DESKTOP,
            },
            lockIcon: false,
            hidden: false,
        },
    ],
});

const getSearchGroup = (
    routes: Record<keyof typeof searchAdvertisingRoutes, string> = searchAdvertisingRoutes,
): INavItem => ({
    title: "ad.intelligence.search.title",
    name: "search",
    hidden: isHidden((swSettings as ISwSettings).components.AdIntSearch),
    subItems: [
        {
            title: "search.advertising.monitor.keywords.title",
            name: "monitor.keywords",
            state: routes.monitorKeywordsHome,
            subItems: [
                {
                    title: "search.advertising.monitor.keywords.title",
                    name: "monitor.keywords.report",
                    state: routes.monitorKeywords,
                    hidden: true,
                },
            ],
        },
        {
            title: "search.advertising.keyword.landing.pages.title",
            name: "keyword.landing.pages",
            state: routes.keywordLandingPages,
        },
        {
            title: "search.advertising.serp.players.title",
            name: "serp.players",
            state: routes.serpPlayers,
        },
        {
            title: "search.advertising.keyword.ads.title",
            name: "keyword.ads",
            state: routes.keywordAds,
        },
        {
            title: "search.advertising.topics.title",
            name: "topics",
            state: routes.topics,
        },
        {
            title: "search.advertising.keywords.title",
            name: "keywords",
            state: routes.keywords,
        },
        {
            title: "search.advertising.landing.pages.title",
            name: "landing.pages",
            state: routes.landingPages,
        },
        {
            title: "search.advertising.search.ads.title",
            name: "search.ads",
            state: routes.textAndShoppingAds,
            defaultQueryParams: {
                webSource: devicesTypes.DESKTOP,
            },
        },
    ],
});

// Social and AI are channel groups holding a single "Ads" report each, so they read like the
// Display and Search groups rather than as standalone links. The entitlement lock lives on the
// report, not on the group.
const getSocialGroup = (
    isGroupHidden: boolean,
    isNoTouch?: boolean,
    socialAdsState: string = SOCIAL_CHANNELS_ROUTES.ADS.stateName,
): INavItem => ({
    title: "ad.intelligence.social.title",
    name: "social",
    hidden: isGroupHidden,
    subItems: [
        {
            title: "ad.intelligence.advertiser.ad.widget.title",
            name: "social.ads",
            state: socialAdsState,
            isNew: !isNoTouch,
            lockIcon: isNoTouch,
            isLocked: isNoTouch,
            onClickLocked: () => openHooksModalV5("AdvertiserActivityUpgradeToTouch"),
        },
    ],
});

// The AI ads gallery is gated by its own claim: hidden users lose the group entirely, hook users
// keep it with a crown that opens the upgrade modal.
const getAiGroup = (isGroupHidden: boolean, aiAdsState: string = AI_CHANNELS_ROUTES.ADS.stateName): INavItem => {
    const { isHidden: isAiAdsHidden, isLocked: isAiAdsLocked } = getAiAdsAvailability();

    return {
        title: "ad.intelligence.llms.title",
        name: "ai",
        hidden: isGroupHidden || isAiAdsHidden,
        subItems: [
            {
                title: "ad.intelligence.advertiser.ad.widget.title",
                name: "ai.ads",
                state: aiAdsState,
                isNew: !isAiAdsLocked,
                lockIcon: isAiAdsLocked,
                isLocked: isAiAdsLocked,
                onClickLocked: () => openHooksModalV5("AdvertiserActivityUpgradeToTouch"),
            },
        ],
    };
};

export type AdvertiserActivityNavBarOverrides = {
    homeState?: string;
    routeOverrides?: AANavBarRouteOverrides;
};

export const digitalSuiteAdvertiserActivityNavBarContent = (
    overrides?: AdvertiserActivityNavBarOverrides,
): ISecondaryBarContents => ({
    SecondaryBarHeader: (
        <DigitalSuiteGenericBarHeader
            state={overrides?.homeState ?? advertiserActivityRoutes.home}
            title={"primary.sidebar.advertiser.activity.title"}
        />
    ),
    SecondaryBarBody: (
        <DigitalSuiteGenericNavBarBody navObj={advertiserActivityNavObj(overrides?.routeOverrides).navList} />
    ),
});

export const advertiserActivityNavObj = (overrides?: AANavBarRouteOverrides): { navList: INavItem[] } => {
    const isNonSearchGroupHidden = getIsNonSearchGroupHidden();
    const isUserNoTouch = isNoTouch(swSettings);
    const displayRoutes = { ...displayAdvertisingStateNames, ...overrides?.display };
    const searchRoutes = { ...searchAdvertisingRoutes, ...overrides?.search };
    const socialAdsState = overrides?.social?.ads ?? SOCIAL_CHANNELS_ROUTES.ADS.stateName;
    const aiAdsState = overrides?.ai?.ads ?? AI_CHANNELS_ROUTES.ADS.stateName;
    return {
        navList: [
            {
                title: "ad.intelligence.advertiser.activity.overview.title",
                name: "overview",
                state: overrides?.overview ?? advertiserActivityRoutes.overview,
                itemComponent: SecondaryItem,
                itemType: ESecondaryItemType.Standalone,
                options: {
                    isUSStatesSupported: true,
                    isVirtualSupported: false,
                },
                overrideParams: {
                    webSource: devicesTypes.TOTAL,
                },
                lockIcon: false,
                hidden: isNonSearchGroupHidden,
            },
            getDisplayGroup(isNonSearchGroupHidden, displayRoutes),
            getSocialGroup(isNonSearchGroupHidden, isUserNoTouch, socialAdsState),
            getAiGroup(isNonSearchGroupHidden, aiAdsState),
            getSearchGroup(searchRoutes),
        ].map(applyNavItemPermissions) as INavItem[],
    };
};
